import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Host,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { sort } from 'fast-sort';
import {
  deepCopy,
  isNullOrEmpty,
} from '../../../../core/utils/common-functions';

import { PrimeTemplate, SelectItem, SortMeta, TableState } from 'primeng/api';
import { DomHandler } from 'primeng/dom';
import { ReorderableColumn, Table, TableService } from 'primeng/table';
import { UserProfileServiceApi } from '../../../../common-api/system-management-api/api/userProfile.service';
import { LocalStorageService } from '../../../../common/services/local-storage.service';
import { BaseComponent } from '../../../../shared/components';

import { ColumnInfo } from '../../../../core/models';
import { UserTraceLogService } from '../../../../rt-shared/services';
import { LogType } from '../../../enums';

//#region Override ReorderableColumn class defined methods
ReorderableColumn.prototype.ngAfterViewInit = function () {
  this.bindEvents();
};

ReorderableColumn.prototype.onMouseDown = function (event) {
  if (this.isEnabled() === false) {
    return;
  }
  if (
    event.target.nodeName === 'INPUT' ||
    event.target.nodeName === 'TEXTAREA' ||
    DomHandler.hasClass(event.target, 'p-column-resizer')
  )
    this.el.nativeElement.draggable = false;
  else this.el.nativeElement.draggable = true;
};

ReorderableColumn.prototype.onDragStart = function (event) {
  if (this.isEnabled() === false) {
    return;
  }
  this.dt.onColumnDragStart(event, this.el.nativeElement);
};

ReorderableColumn.prototype.onDragOver = function (event) {
  if (this.isEnabled() === false) {
    return;
  }
  event.preventDefault();
};

ReorderableColumn.prototype.onDragEnter = function (event) {
  if (this.isEnabled() === false) {
    return;
  }
  this.dt.onColumnDragEnter(event, this.el.nativeElement);
};

ReorderableColumn.prototype.onDragLeave = function (event) {
  if (this.isEnabled() === false) {
    return;
  }
  this.dt.onColumnDragLeave(event);
};
//#endregion Override ReorderableColumn functions

export function tableFactory(dataTable: NotifyDataTableComponent): Table {
  return dataTable.table;
}

const ALL_DROPDOWN_OPTION: SelectItem = { label: 'All', value: 'all' };

/**
 * 資料表元件
 *
 * @export
 * @class DataTableComponent
 * @extends {BaseComponent} 基底元件
 * @implements {OnChanges} Angular OnChanges 生命週期
 * @implements {OnInit} Angular OnInit 生命週期
 * @implements {AfterContentInit} Angular AfterContentInit 生命週期
 * @implements {AfterViewInit} Angular AfterViewInit 生命週期
 */
@Component({
  selector: 'sigv-notify-data-table',
  templateUrl: './notify-data-table.component.html',
  styleUrls: ['./notify-data-table.component.scss'],
  providers: [
    TableService,
    {
      provide: Table,
      useFactory: tableFactory,
      deps: [NotifyDataTableComponent],
    },
  ],
})
export class NotifyDataTableComponent
  extends BaseComponent
  implements OnChanges, OnInit, AfterViewInit
{
  /**
   * 欄位資訊清單
   * 必填
   */
  @Input() columns: ColumnInfo[] = [];

  /**
   * 資料列清單
   * 必填
   */
  @Input() value: any[] = [];

  /**
   * 是否顯示小燈泡資訊按鈕
   */
  @Input() showInformation = false;

  /**
   * 分頁第一頁第一筆索引
   */
  @Input() first = 0;

  /**
   * 是否可選取資料列
   */
  @Input() selectable = false;

  /**
   * 是否可編輯資料列
   */
  @Input() editable = false;

  /**
   * 是否限制為單筆編輯
   */
  @Input() singleEdit = false;

  /**
   * 是否可拖拉欄位
   */
  @Input() reorderableColumns = true;

  /**
   * 資料列唯一識別屬性
   * 註:此屬性必須設定，否則會造成許多操作無法識別資料列
   */
  @Input() dataKey = '';

  /**
   * 所選取的資料列清單
   */
  @Input() selection: any = [];

  /**
   * 是否分頁
   */
  @Input() paginator = false;

  /**
   * 是否可新增資料列
   * 註:新增功能不可使用 server side 分頁，因為新增的暫存列無法知道要放在哪一個分頁中
   */
  @Input() canCreateRow = false;

  /**
   * 是否可刪除資料列
   * selectable 需設定為 true
   * dataKey 需設定
   */
  @Input() canDelete = false;

  /**
   * 是否顯示已讀按鈕
   * selectable 需設定為 true
   * dataKey 需設定
   */
  @Input() canShowRead = false;

  /**
   * 個人化設定檔名稱
   * 當有設定 preferenceKey 值，才會在變動欄位順序或變更欄位顯示時儲存欄位設定值，
   * 建議命名規則：{系統名稱}_{功能名稱}_{資料表用途}_Table，EX: Collection_CaseList_QueryResult_Table
   * 注意：preferenceKey 值不可重複，若與其他資料表的 preferenceKey 值重複會導致讀取欄位設定發生錯誤
   */
  @Input() preferenceKey: any;

  /**
   * 是否使用延遲載入
   */
  @Input() lazy = false;

  /**
   * 是否顯示讀取圖示
   */
  @Input() loading = false;

  /**
   * 是否啟用 Scroll
   */
  @Input() scrollable = false;

  /**
   * 固定 Scroll 高度值
   */
  @Input() scrollHeight = 'auto';

  /**
   * 凍結欄數
   */
  @Input() frozenColCount = 0;

  /**
   * 新增資料列方法
   */
  @Input() createRowFunc?: () => any;

  /**
   * 判斷是否顯示資料列 checkbox 方法
   */
  @Input() displayRowChkboxFunc: (row: any, index: number) => boolean =
    this.defaultBooleanFunc;

  /**
   * 判斷是否顯示資料列編輯按鈕方法
   */
  @Input() displayRowEditBtnFunc: (row: any, index: number) => boolean =
    this.defaultBooleanFunc;

  /**
   * 檢核編輯資料列方法
   */
  @Input() validateRowCreateFunc: (row?: any) => boolean =
    this.defaultBooleanFunc;

  /**
   * 檢核新增資料列方法
   */
  @Input() validateRowEditFunc: (row: any, index: number) => boolean =
    this.defaultBooleanFunc;

  /**
   * 開啟視窗編輯
   */
  @Input() editWithDialog = false;

  /**
   * 開啟視窗新增
   */
  @Input() createWithDailog = true;

  /**
   * 顯示欄位篩選
   */
  @Input() showColumnFilter = false;

  /**
   * 提示資訊文字內容
   */
  @Input() extraInformationText?: string;

  /**
   * 隱藏總筆數資訊(在非分頁模式下才有作用)
   */
  @Input() hideTotalRecordsReport = true;

  /**
   * 資料列選取事件
   */
  @Output() selectionChange = new EventEmitter<any>();

  /**
   * 欄位排序事件
   */
  @Output() tableSort = new EventEmitter<any>();

  /**
   * 資料分頁事件
   */
  @Output() tablePaging = new EventEmitter<any>();

  /**
   * 資料變更事件
   */
  @Output() valueChange = new EventEmitter<any>();

  /**
   * 資料列已讀事件
   */
  @Output() rowRead = new EventEmitter<any>();

  /**
   * 資料列刪除事件
   */
  @Output() rowDelete = new EventEmitter<any>();

  /**
   * 分頁第一頁第一筆索引變更事件
   */
  @Output() firstChange = new EventEmitter<any>();

  /**
   * 資料列編輯事件
   */
  @Output() rowEdit = new EventEmitter<any>();

  /**
   * 資料列編輯確認事件
   */
  @Output() rowEditSave = new EventEmitter<any>();

  /**
   * 資料列編輯取消事件
   *
   * @memberof DataTableComponent
   */
  @Output() rowEditCancel = new EventEmitter<any>();

  /**
   * 資料列新增事件
   */
  @Output() rowCreate = new EventEmitter<any>();

  /**
   * 資料列新增確認事件
   */
  @Output() rowCreateSave = new EventEmitter<any>();

  /**
   * 新增 Dialog 確認事件
   */
  @Output() rowDialogConfirm = new EventEmitter<any>();

  /**
   * 新增 Dialog 取消事件
   */
  @Output() rowDialogCancel = new EventEmitter<any>();

  /**
   * 注入的 PrimeTemplate 清單
   */
  @ContentChildren(PrimeTemplate) templates!: QueryList<PrimeTemplate>;

  /**
   * 元件內的 p-table 元件
   */
  @ViewChild(Table) table!: Table;

  /**
   * 注入的工具列 Template
   */
  toolbarTemplate!: TemplateRef<any>;

  /**
   * 注入的標題欄 Template
   */
  captionTemplate!: TemplateRef<any>;

  /**
   * 注入的表身 Template
   */
  bodyTemplate!: TemplateRef<any>;

  /**
   * 注入的表頭 Template
   */
  headerTemplate!: TemplateRef<any>;

  /**
   * 注入的表頭欄位內容 Template
   */
  columnHeaderContentTemplate!: TemplateRef<any>;

  /**
   * 注入的新增列 Dialog 內容 Template
   */
  createDialogContentTemplate!: TemplateRef<any>;

  /**
   * 是否顯示欄位篩選
   */
  isOpenColumnFilter = false;

  /**
   * 欄位篩選選單選項清單
   */
  filterColumnOptions: SelectItem[] = [];

  /**
   * 選擇顯示的欄位清單
   */
  selectedFields: string[] = [];

  /**
   * 暫存編輯的資料列
   */
  clonedRows: { [s: string]: any } = {};

  /**
   * 是否顯示新增列 Dialog
   */
  showCreateDialog = false;

  /**
   * 分頁頁數選項清單
   */
  rowsPerPageItems: any[] = [10, 25, 50];

  tooltipText = '';

  /**
   * 資料總筆數
   */
  private totalRecordsCount?: number;

  private editRow = false;

  private currentEditingRowIndex?: number;

  private currentEditingRow?: any;

  /**
   * 建構子
   * @param {ElementRef} el
   * @param {Injector} injector
   * @param {ChangeDetectorRef} cd
   * @param {LocalStorageService} localStorageService
   * @param {Renderer2} renderer
   * @memberof DataTableComponent
   */
  constructor(
    @Host() private el: ElementRef,
    private injector: Injector,
    private userTraceService: UserTraceLogService,
    private cd: ChangeDetectorRef,
    private localStorageService: LocalStorageService,
    private userProfileService: UserProfileServiceApi
  ) {
    super(injector);
  }

  /**
   * 是否為正在編輯狀態
   */
  get isTableEditing(): boolean {
    const hasRowEditing =
      (this.table?.value ?? []).some(
        (x) => this.table.editMode === 'row' && this.table.isRowEditing(x)
      ) ||
      (this.table?.value ?? []).some((x) => x.create) ||
      this.showCreateDialog;
    return hasRowEditing;
  }

  get displayColumns(): ColumnInfo[] {
    return this.table?._columns ?? [];
  }

  get checkboxAvalaible(): boolean {
    return (
      (this.value ?? []).filter((x, i) => this.displayRowChkboxFunc(x, i))
        .length > 0
    );
  }

  /**
   * 取得外層元素高度
   */
  get wrapperHeight(): any {
    if (this.scrollHeight === 'flex') {
      return this.el.nativeElement.parentElement.offsetHeight + 'px';
    }
    return this.scrollHeight;
  }

  /**
   * 是否顯示刪除按鈕
   */
  get showDelete(): boolean {
    return this.selectable && this.canDelete && this.selection?.length > 0;
  }

  /**
   * 是否顯示已讀按鈕
   */
  get showRead(): boolean {
    return this.selectable && this.canShowRead && this.selection?.length > 0;
  }

  /**
   * 是否顯示排序相關元素
   */
  get sort(): boolean {
    return this.columns?.filter((x) => x.sortable).length > 0;
  }

  /**
   * 是否顯示複雜資料表呈現
   */
  get complex(): boolean {
    return this.columns?.length > 8;
  }

  /**
   * 是否顯示分頁相關元素
   */
  get paging(): boolean {
    return this.paginator && this.value.length > 0;
  }

  /**
   * 是否要存取個人化設定
   */
  get canSavePreference(): boolean {
    return isNullOrEmpty(this.preferenceKey) === false;
  }

  /**
   * 取得資料總筆數
   */
  get totalRecords(): number {
    return this.totalRecordsCount ?? this.value.length ?? 0;
  }

  /**
   * 設定資料總筆數
   */
  @Input() set totalRecords(value: number) {
    this.totalRecordsCount = value;
  }

  /**
   * 取得資料總筆數報告
   */
  get totalRecordsReport(): string {
    const recordsReportStr = this.translateService
      .instant('Total {totalRecords} Records')
      .replace('{totalRecords}', this.totalRecords.toString());
    return recordsReportStr;
  }

  /**
   * 動態取得 Colspan 數值
   */
  get colSpanCount(): number {
    return this.displayColumns?.length + (this.hasFunctionCell ? 1 : 0);
  }

  /**
   * 是否有未暫存的資料列
   */
  get hasUnConfirmedNewRows(): boolean {
    return this.value?.filter((x) => x.create).length > 0;
  }

  get selectableRows(): any[] {
    return this.value.filter((x, i) => this.displayRowChkboxFunc(x, i));
  }

  /**
   * 是否要加入功能格
   */
  get hasFunctionCell(): boolean {
    return this.selectable || this.editable;
  }

  /**
   * 資料排序資訊
   */
  get multiSortMeta(): SortMeta[] {
    return this.table.multiSortMeta ?? [];
  }

  /**
   * OnChnages 生命週期(實作 OnChnages 介面)
   *
   * @memberof DataTableComponent
   */
  ngOnChanges(changes: SimpleChanges): void {
    // 偵測 extraInformationText 值的變化
    if (!isNullOrEmpty(changes?.['extraInformationText'])) {
      this.tooltipText = this.renderInformationContext();
    }

    // 偵測 selectable / editable / frozenColCount 值的變化
    if (
      !isNullOrEmpty(changes?.['selectable']) ||
      !isNullOrEmpty(changes?.['editable']) ||
      !isNullOrEmpty(changes?.['frozenColCount'])
    ) {
      if (this.hasFunctionCell) {
        this.frozenColCount =
          this.frozenColCount === 0 ? 1 : this.frozenColCount;
      }
    }
  }

  /**
   * OnInit 生命週期(實作 OnInit 介面)
   *
   * @memberof DataTableComponent
   */
  ngOnInit(): void {
    this.columns = this.columns.map((item, index) => ({
      ...item,
      sortNo: index,
    }));
    this.tooltipText = this.renderInformationContext();
    this.translateService.onLangChange.subscribe({
      next: (event: any) => {
        this.tooltipText = this.renderInformationContext();
      },
    });
  }

  /**
   * AfterViewInit 生命週期(實作 AfterViewInit 介面)
   *
   * @memberof DataTableComponent
   */
  ngAfterViewInit(): void {
    this.overrideTableFunctions();

    this.table.restoreState();
    this.table.restoreColumnOrder();
    if (this.sort) {
      this.table.sortMultiple();
    }

    if (this.complex || this.showColumnFilter) {
      // 超過 8 欄或指定要顯示篩選欄位
      let hiddenFields = this.columns
        .filter((x) => x.hidden)
        .map((x) => x.field!);
      if (this.canSavePreference) {
        const tablePreference = this.getTablePreference();
        if (tablePreference) {
          const columnOrders = (tablePreference.columnOrder ?? []) as string[];
          hiddenFields = hiddenFields.filter(
            (x) => columnOrders.includes(x) === false
          );
        }
      }
      this.table._columns = this.displayColumns.filter(
        (x) => hiddenFields.includes(x.field!) === false
      );
      this.selectedFields = this.table._columns.map((x) => x.field!);

      const invisibleOptionValues = this.table._columns
        .map((x) => x.field!)
        .slice(
          0,
          this.hasFunctionCell ? this.frozenColCount - 1 : this.frozenColCount
        );
      const visibleOptions: SelectItem[] = this.columns
        .filter((x) => invisibleOptionValues.includes(x.field!) === false)
        .map((x) => ({ label: x.header, value: x.field })) as SelectItem[];

      this.filterColumnOptions = [ALL_DROPDOWN_OPTION, ...visibleOptions];
      if (
        this.selectedFields.length -
          this.frozenColCount -
          (this.hasFunctionCell ? -1 : 0) ===
        this.filterColumnOptions.length - 1
      ) {
        this.selectedFields.push(ALL_DROPDOWN_OPTION.value);
      }
    } else {
      // 小於 8 個欄位且不顯示篩選欄位
      // 如果有存 preference，除了要還原 preference 設定外還要將沒在設定裡的顯示出來
      if (this.canSavePreference) {
        const tablePreference = this.getTablePreference();
        if (tablePreference) {
          const columnOrders = (tablePreference.columnOrder ?? []) as string[];
          const uncoveredColumns = this.columns.filter(
            (x) => columnOrders.includes(x.field!) === false
          );
          this.table._columns = this.displayColumns.concat(uncoveredColumns);
        }
      }
    }

    this.templates.forEach((item) => {
      switch (item.getType()) {
        case 'toolbar':
          this.toolbarTemplate = item.template;
          break;
        case 'caption':
          this.captionTemplate = item.template;
          break;
        case 'header':
          this.headerTemplate = item.template;
          break;
        case 'headerContent':
          this.columnHeaderContentTemplate = item.template;
          break;
        case 'body':
          this.bodyTemplate = item.template;
          break;
        case 'createDialogContent':
          this.createDialogContentTemplate = item.template;
          break;
        default:
          break;
      }
    });

    if (this.scrollable) {
      if (this.scrollHeight !== 'flex') {
        this.table.el.nativeElement.querySelector(
          '.p-datatable-wrapper'
        ).style.maxHeight = this.scrollHeight;
      }
      if (this.frozenColCount > 0) {
        this.updateTableWidth();
      }
    }

    this.cd.detectChanges();
  }

  reset(): void {
    this.table.reset();
    this.table.saveState();
  }

  /**
   * 欄位選擇按鈕 Click
   * button click event
   * @param {*} event
   * @memberof DataTableComponent
   */
  onColumnFilterBtnClick(event: any): void {
    this.isOpenColumnFilter = !this.isOpenColumnFilter;
    event.stopPropagation();
  }

  /**
   * 欄位選單隱藏
   * p-multiSelect onPanelHide event
   * @param {*} event
   * @memberof DataTableComponent
   */
  onMultiSelectClose(event: any): void {
    this.table.saveState();
    this.isOpenColumnFilter = false;
  }

  /**
   * 欄位選單選項選取
   * p-multiSelecg onChange event
   * @param {*} event
   * @memberof DataTableComponent
   */
  onMultiSelectChange(event: any): void {
    const value = event.itemValue;
    if (this.displayColumns.findIndex((x) => x.field === value) > -1) {
      // 取消勾選要拿掉排序設定
      const tmpMultiSortMeta =
        this.table.multiSortMeta?.filter((x) => x.field !== value) ?? [];
      if (tmpMultiSortMeta.length > 0) {
        this.table.multiSortMeta = tmpMultiSortMeta;
      } else {
        (this.table as any).multiSortMeta = null;
      }
    }
    if (this.selectedFields.some((x) => x === ALL_DROPDOWN_OPTION.value)) {
      // 剛好勾全選或從全選取消一個
      if (value === ALL_DROPDOWN_OPTION.value) {
        // 勾全選
        const unSelectedFields = this.filterColumnOptions
          .map((x) => x.value)
          .filter((x) => this.selectedFields.includes(x) === false);
        this.selectedFields = [...this.selectedFields, ...unSelectedFields];
      } else {
        // 從全選取消一個
        if (
          this.selectedFields.length -
            this.frozenColCount -
            (this.hasFunctionCell ? -1 : 0) <
          this.filterColumnOptions.length
        ) {
          // 取消一個剛好變非全選
          this.selectedFields = this.selectedFields.filter(
            (x) => x !== ALL_DROPDOWN_OPTION.value
          );
        }
      }
    } else {
      // 其他情況
      if (value === ALL_DROPDOWN_OPTION.value) {
        // 取消全選
        this.selectedFields =
          this.frozenColCount > 0
            ? this.selectedFields.slice(
                0,
                this.hasFunctionCell
                  ? this.frozenColCount - 1
                  : this.frozenColCount
              )
            : [];
      } else {
        // 勾其他
        if (
          this.selectedFields.length -
            this.frozenColCount -
            (this.hasFunctionCell ? -1 : 0) ===
          this.filterColumnOptions.length - 1
        ) {
          // 勾起來剛好全部全選
          this.selectedFields = [
            ALL_DROPDOWN_OPTION.value,
            ...this.selectedFields,
          ];
        }
      }
    }

    let updatedDisplayColumns: ColumnInfo[] = [];
    if (this.reorderableColumns) {
      // 欄位可拖拉：重新勾選的欄位，需從最後一欄加入
      const uncheckedColumns = this.columns.filter(
        (x) => this.displayColumns.findIndex((y) => y.field === x.field) < 0
      );
      updatedDisplayColumns = [
        ...this.displayColumns,
        ...uncheckedColumns,
      ].filter((x) => this.selectedFields.includes(x.field!));
    } else {
      // 欄位不可拖拉：重新勾選的欄位，需依照原先排序顯示
      updatedDisplayColumns = sort(
        this.selectedFields.reduce((sum: ColumnInfo[], item: string) => {
          const target = this.columns.find((x) => x.field === item);
          if (isNullOrEmpty(target) === false) {
            sum.push(target!);
          }
          return sum;
        }, [])
      ).asc((x) => x.sortNo);
    }

    this.table._columns = updatedDisplayColumns;

    if (this.scrollable && this.frozenColCount > 0) {
      this.updateTableWidth();
    }
  }

  /**
   * 資料列選取
   * p-table selectionChange event
   * @param {*} event
   * @memberof DataTableComponent
   */
  onSelectionChange(event: any): void {
    const selection = event.filter((row: any) => {
      const idx = this.value.findIndex((x) => x === row);
      return this.displayRowChkboxFunc(row, idx);
    });
    this.selectionChange.emit(selection);
  }

  /**
   * 欄位排序
   * p-table onSort event
   * @param {*} event
   * @memberof DataTableComponent
   */
  onTableSort(event: any): void {
    this.tableSort.emit(event);
    // 有未暫存的新增輸入列要移到最後一筆
    if (this.hasUnConfirmedNewRows) {
      const idx = this.table.value.findIndex((x) => x.create);
      const createRow = this.table.value.splice(idx, 1)?.[0];
      this.table.value.push(createRow);
    }
  }

  /**
   * 資料分頁
   * p-table onPage event
   * @param {*} event
   * @memberof DataTableComponent
   */
  onTablePaging(event: any): void {
    this.tablePaging.emit(event);
  }

  /**
   * 變動分頁第一頁
   * p-table firstChange event
   * @param {*} event
   * @memberof DataTableComponent
   */
  onFirstChange(event: any): void {
    this.firstChange.emit(event);
  }

  /**
   * 編輯資料列(Dialog)
   * button click event
   * @param {*} row
   * @memberof DataTableComponent
   */
  onRowEditDialogInit(row: any): void {
    this.logger.debug('onRowEditDialogInit', row);
    this.editRow = true;
    this.currentEditingRow = deepCopy(row);
    this.showCreateDialog = true;
    this.rowEdit.emit(this.currentEditingRow);
  }

  /**
   * 編輯資料列(列)
   * button click event
   * @param {*} row
   * @param {number} index
   * @memberof DataTableComponent
   */
  onRowEditInit(row: any, index: number): void {
    this.logger.debug('onRowEditInit', row);
    this.clonedRows[row[this.dataKey]] = { ...row };
    this.rowEdit.emit(row);
  }

  /**
   * 儲存編輯資料列
   * button click event
   * @param {*} row
   * @param {number} index
   * @return {*}  {void}
   * @memberof DataTableComponent
   */
  onRowEditSave(row: any, index: number): void {
    this.logger.debug('onRowEditSave', row);
    // 編輯檢核失敗不關閉
    if (this.validateRowEditFunc(row, index) === false) {
      this.table.initRowEdit(row);
      return;
    }
    delete this.clonedRows[row[this.dataKey]];
    this.cd.detectChanges();
    this.userTraceService.setTraceLog(LogType.SERVICE_ACTION, {
      onRowEditSave: this.value,
      row,
    });
    this.valueChange.emit(this.value);
    this.rowEditSave.emit(row);
  }

  /**
   * 取消編輯資料列
   * button click event
   * @param {*} row
   * @param {number} index
   * @memberof DataTableComponent
   */
  onRowEditCancel(row: any, index: number): void {
    this.logger.debug('onRowEditCancel', row);
    this.value[index] = this.clonedRows[row[this.dataKey]];
    delete this.clonedRows[row[this.dataKey]];
    this.cd.detectChanges();
    this.valueChange.emit(this.value);
    this.rowEditCancel.emit(row);
  }

  /**
   * 儲存新增資料列
   * button click event
   * @param {*} row
   * @memberof DataTableComponent
   */
  onRowCreateSave(row: any): void {
    this.logger.debug('onRowCreateSave', row);
    // 新增檢核失敗不關閉
    if (this.validateRowCreateFunc(row) === false) {
      this.table.initRowEdit(row);
      return;
    }
    delete row.create;
    this.cd.detectChanges();
    this.valueChange.emit(this.value);
    this.rowCreateSave.emit(row);
    this.table.initRowEdit(row);
  }

  /**
   * 取消新增資料列
   * button click event
   * @param {*} row
   * @param {number} index
   * @memberof DataTableComponent
   */
  onRowCreateCancel(row: any, index: number): void {
    const idx = this.value.findIndex((x) => x.create);
    this.value.splice(idx, 1);
    this.table.value = this.value;
    // 跳到最後一頁(因為有時候輸入列是在新分頁的第一筆，取消後該新分頁就消失了)
    this.first =
      (Math.ceil(this.totalRecords / this.table._rows) - 1) * this.table._rows;
    this.table.first = this.first;
    this.cd.detectChanges();
    this.firstChange.emit(this.first);
  }

  /**
   * 新增資料列
   * button click event
   * @return {*}  {void}
   * @memberof DataTableComponent
   */
  onCreateRowBtnClick(): void {
    if (this.complex && this.createWithDailog) {
      this.showCreateDialog = true;
      this.rowCreate.emit();
      return;
    }
    this.addNewRow();
    this.rowCreate.emit();
  }

  /**
   * 新增資料表單 Dialog 確認
   * sigv-content-dialog confirm event
   * @memberof DataTableComponent
   */
  onCreateRowDialogConfirm(): void {
    if (this.editRow) {
      // 編輯模式

      // 編輯檢核失敗不關閉
      if (
        this.validateRowEditFunc(
          this.currentEditingRow,
          this.currentEditingRowIndex!
        ) === false
      ) {
        return;
      }

      // 將編輯後的資料列覆寫
      const idx = this.value.findIndex(
        (x) => x[this.dataKey] === this.currentEditingRow[this.dataKey]
      );
      if (idx > -1) {
        this.value[idx] = this.currentEditingRow;
      }
      this.value = [...this.value];
      this.valueChange.emit(this.value);
      this.rowEditSave.emit(this.currentEditingRow);
      delete this.currentEditingRowIndex;
      delete this.currentEditingRow;
    } else {
      // 新增模式

      // 新增檢核失敗不關閉
      if (this.validateRowCreateFunc() === false) {
        return;
      }

      const row = this.addNewRow();
      this.rowCreateSave.emit(row);
    }
    this.editRow = false;
    this.showCreateDialog = false;
    this.rowDialogConfirm.emit();
  }

  /**
   * 新增資料表單 Dialog 取消
   * sigv-content-dialog cancel event
   * @memberof DataTableComponent
   */
  onCreateRowDialogCancel(): void {
    if (this.editRow) {
      this.rowEditCancel.emit(this.currentEditingRow);
      delete this.currentEditingRow;
    }
    this.editRow = false;
    this.rowDialogCancel.emit();
  }

  /**
   * 儲存資料表設定
   * p-table onStateSave event
   * @param {*} event
   * @memberof DataTableComponent
   */
  onTableStateSave(event: any): void {}

  /**
   * 讀取資料表設定
   * p-table onStateRestore event
   * @param {*} event
   * @memberof DataTableComponent
   */
  onTableStateRestore(event: any): void {}

  /**
   * 欄位重新排序
   * p-table onColReorder event
   * @param {*} event
   * @memberof DataTableComponent
   */
  onColReorder(event: any): void {}

  /**
   * 刪除按鈕 Click
   * button click event
   * @memberof DataTableComponent
   */
  onDeleteBtnClick(): void {
    this.logger.debug('onDeleteBtnClick', this.selection);
    const deleteKeys = (this.selection as any[]).map((x) => x[this.dataKey]);
    const deletedRows = this.value.filter((x) =>
      deleteKeys.includes(x[this.dataKey])
    );
    this.value = this.value.filter(
      (x) => deleteKeys.includes(x[this.dataKey]) === false
    );
    this.cd.detectChanges();
    this.selectionChange.emit(this.selection);
    this.valueChange.emit(this.value);
    this.rowDelete.emit(deletedRows);
    // 跳到正確頁(因為有時候刪掉列之後導致分頁消失)
    if (this.first >= this.totalRecords) {
      this.first =
        (Math.ceil(this.totalRecords / this.table._rows) - 1) *
        this.table._rows;
      this.table.first = this.first;
      this.firstChange.emit(this.first);
    }
  }

  /**
   * 已讀按鈕 Click
   * button click event
   * @memberof DataTableComponent
   */
  onReadBtnClick(): void {
    this.logger.debug('onReadBtnClick', this.selection);
    const readKeys = (this.selection as any[]).map((x) => x[this.dataKey]);
    const readRows = this.value.filter((x) =>
      readKeys.includes(x[this.dataKey])
    );
    this.value = this.value.filter(
      (x) => readKeys.includes(x[this.dataKey]) === false
    );
    this.cd.detectChanges();
    this.selectionChange.emit(this.selection);
    this.valueChange.emit(this.value);
    this.rowRead.emit(readRows);
    // 跳到正確頁(因為有時候刪掉列之後導致分頁消失)
    if (this.first >= this.totalRecords) {
      this.first =
        (Math.ceil(this.totalRecords / this.table._rows) - 1) *
        this.table._rows;
      this.table.first = this.first;
      this.firstChange.emit(this.first);
    }
  }

  /**
   * 取得 table preference 資料
   *
   * @private
   * @return {*}
   * @memberof DataTableComponent
   */
  private getTablePreference() {
    const preferences = this.localStorageService.getItem('preferences') ?? {};
    const preferenceObj: any = {};
    preferenceObj.table = preferenceObj.table ?? {};
    const tablePreference = preferenceObj.table.hasOwnProperty(
      this.preferenceKey
    )
      ? preferenceObj.table[this.preferenceKey]
      : null;

    return tablePreference;
  }

  /**
   * 新增列動作
   *
   * @private
   * @return {*}  {void}
   * @memberof DataTableComponent
   */
  addNewRow(): any {
    this.logger.debug('addNewRow', this.value);
    if (isNullOrEmpty(this.createRowFunc)) {
      throw new Error('createRowFunc is null.');
    }
    const newRow = this.createRowFunc!();
    if (isNullOrEmpty(newRow)) {
      return null;
    }
    if (this.complex && this.createWithDailog) {
      this.value = [...this.value, newRow];
      this.valueChange.emit(this.value);
      return newRow;
    }
    newRow.create = true;
    this.value = [...this.value, newRow];
    this.cd.detectChanges();
    this.valueChange.emit(this.value);
    // 跳到最後一頁
    this.first =
      (Math.ceil(this.totalRecords / this.table._rows) - 1) * this.table._rows;
    this.table.first = this.first;
    this.firstChange.emit(this.first);

    return newRow;
  }

  /**
   * 更新p-table寬度(欄位凍結時使用)
   *
   * @private
   * @memberof DataTableComponent
   */
  private updateTableWidth(): void {
    let tableWidth = 0;
    this.displayColumns.forEach((col) => {
      tableWidth += isNullOrEmpty(col.width)
        ? 155
        : parseInt(col.width!.replace('px', ''), 10);
    });
    if (this.hasFunctionCell) {
      tableWidth += 120;
    }
    this.table.tableStyle = {
      'min-width': tableWidth + 'px',
      'max-width': '100%',
    };
  }

  /**
   * 計算並回傳凍結欄位時的CSS樣式
   *
   * @param {number} index
   * @param {boolean} [isHeader]
   * @param {boolean} [isSelectCol]
   * @return {*}  {{[klass: string]: any;}}
   * @memberof DataTableComponent
   */
  calcFrozenColumnStyles(
    displayColIndex: number,
    isHeader?: boolean,
    isSelectCol?: boolean
  ): { [klass: string]: any } {
    const isFrozenCol =
      displayColIndex <
      (this.hasFunctionCell ? this.frozenColCount - 1 : this.frozenColCount);

    if (isSelectCol) {
      return {
        width: '120px',
        'min-width': '120px',
        left: '0px',
        position: 'sticky',
        'z-index': 1,
      };
    } else {
      return {
        width: this.displayColumns[displayColIndex]?.width ?? '155px',
        'min-width': this.displayColumns[displayColIndex]?.width ?? '155px',
        left: isFrozenCol
          ? this.displayColumns
              .slice(0, displayColIndex)
              .reduce(
                (sum, current) =>
                  sum +
                  (isNullOrEmpty(current.width)
                    ? 155
                    : parseInt(current.width!.replace('px', ''), 10)),
                this.hasFunctionCell ? 120 : 0
              ) + 'px'
          : null,
        position: isFrozenCol ? 'sticky' : null,
        'z-index': isFrozenCol ? 1 : null,
      };
    }
  }

  defaultBooleanFunc(): boolean {
    return true;
  }

  private renderInformationContext(): string {
    const baseText =
      this.sort || this.reorderableColumns
        ? this.translateService.instant(
            'Table Sorting/Filtering Tooltip Content'
          )
        : '';
    const context = isNullOrEmpty(this.extraInformationText)
      ? baseText
      : `${baseText}${baseText === '' ? '' : '<br/><br/>'}${
          this.extraInformationText
        }`;
    return context;
  }

  /**
   * Override primeng table instance methods
   *
   * @private
   * @memberof DataTableComponent
   */
  private overrideTableFunctions(): void {
    //#region Override p-table 儲存個人化設定 function
    this.table.saveState = function () {
      if (isNullOrEmpty(this.stateKey)) {
        return;
      }

      const storage = this.getStorage();
      const userProfileJsonStr = storage.getItem('userProfile');
      if (!userProfileJsonStr) {
        return;
      }
      const userProfile = JSON.parse(userProfileJsonStr);
      const userNo = userProfile?.userNo;

      if (isNullOrEmpty(userNo)) {
        return;
      }

      let state: TableState = {};

      // 儲存排序欄位
      if (this.multiSortMeta) {
        state.multiSortMeta = this.multiSortMeta;
      }

      // 儲存欄位排序
      this.saveColumnOrder(state);

      const jsonStr = storage.getItem('preferences');
      const dateFormat = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/;
      const reviver = function (key: any, value: string | number | Date) {
        if (typeof value === 'string' && dateFormat.test(value)) {
          return new Date(value);
        }
        return value;
      };

      let preferences: any = {};
      if (jsonStr) {
        preferences = JSON.parse(jsonStr, reviver) ?? {};
      }

      let preferenceObj: any = preferences[userNo] ?? {};
      preferenceObj.table = preferenceObj.table ?? {};
      preferenceObj.table[this.stateKey] = state;

      preferences[userNo] = preferenceObj;
      storage.setItem('preferences', JSON.stringify(preferences));

      this.onStateSave.emit(state);
    };
    //#endregion Override p-table 儲存個人化設定 function

    //#region Override p-table 讀取個人化設定 function
    this.table.restoreState = function () {
      if (isNullOrEmpty(this.stateKey)) {
        return;
      }

      const storage = this.getStorage();
      const userProfileJsonStr = storage.getItem('userProfile');
      if (!userProfileJsonStr) {
        return;
      }
      const userProfile = JSON.parse(userProfileJsonStr);
      const userNo = userProfile?.userNo;

      if (isNullOrEmpty(userNo)) {
        return;
      }

      const jsonStr = storage.getItem('preferences');
      const dateFormat = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/;
      const reviver = function (key: any, value: string | number | Date) {
        if (typeof value === 'string' && dateFormat.test(value)) {
          return new Date(value);
        }

        return value;
      };

      let preferences: any = {};
      if (jsonStr) {
        preferences = JSON.parse(jsonStr, reviver) ?? {};
      }

      let preferenceObj: any = preferences[userNo] ?? {};
      preferenceObj.table = preferenceObj.table ?? {};

      if (preferenceObj.table.hasOwnProperty(this.stateKey)) {
        if (preferenceObj.table[this.stateKey]) {
          const state = preferenceObj.table[this.stateKey];

          // 還原欄位排序
          if (state.multiSortMeta) {
            this.restoringSort = true;
            this._multiSortMeta = state.multiSortMeta;
          }
          this.stateRestored = true;
          this.onStateRestore.emit(state);
        }
      }
    };
    //#endregion Override p-table 讀取個人化設定 function

    //#region Override p-table 讀取個人化欄位順序設定 function
    this.table.restoreColumnOrder = function () {
      if (isNullOrEmpty(this.stateKey)) {
        return;
      }

      const storage = this.getStorage();
      const userProfileJsonStr = storage.getItem('userProfile');
      if (!userProfileJsonStr) {
        return;
      }
      const userProfile = JSON.parse(userProfileJsonStr);
      const userNo = userProfile?.userNo;

      if (isNullOrEmpty(userNo)) {
        return;
      }

      if (isNullOrEmpty(userNo)) {
        return;
      }

      const jsonStr = storage.getItem('preferences');
      const dateFormat = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/;
      const reviver = function (key: any, value: string | number | Date) {
        if (typeof value === 'string' && dateFormat.test(value)) {
          return new Date(value);
        }

        return value;
      };

      let preferences: any = {};
      if (jsonStr) {
        preferences = JSON.parse(jsonStr, reviver) ?? {};
      }

      let preferenceObj: any = preferences[userNo] ?? {};
      preferenceObj.table = preferenceObj.table ?? {};

      if (preferenceObj.table.hasOwnProperty(this.stateKey)) {
        if (preferenceObj.table[this.stateKey]) {
          const state = preferenceObj.table[this.stateKey];

          let columnOrder = state.columnOrder;
          if (columnOrder) {
            let reorderedColumns: any[] = [];

            columnOrder.map((key: any) => {
              let col = this.findColumnByKey(key);
              if (col) {
                reorderedColumns.push(col);
              }
            });
            this.columnOrderStateRestored = true;
            this.columns = reorderedColumns;
          }
        }
      }
    };
    //#endregion Override p-table 讀取個人化欄位順序設定 function
  }
}
